import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import { NestedPlaceholder } from 'components/Placeholder';
import { useUserContext } from 'contexts';
import {
	type PlaceholderQueryVars,
	useGlobalLinks,
	useLayoutServicePlaceholder,
} from 'hooks';
import type { GlobalLinkPartial, PartialPopoverProps } from 'models/sitecore';
import type { PartialIdString } from 'types';
import { filterTruthyValues } from 'utils/collection';
import { is } from 'utils/helpers';
import { slugify } from 'utils/string';

import Popover from './Popover';

interface Props<T> {
	heading: string;
	isOpen: boolean;
	onClose: () => void;
	partial: keyof GlobalLinkPartial | (PartialIdString | undefined);
	partialProps?: T;
	partialQueryVars?: PlaceholderQueryVars;
}

/** A popover that loads and displays a partial route from Sitecore. */
export default function PartialPopover<T extends Record<keyof T, unknown>>({
	heading,
	isOpen,
	onClose,
	partial,
	partialProps,
	partialQueryVars,
}: Props<T>) {
	const links = useGlobalLinks();
	const item = partial
		? is.keyOf(links, partial)
			? links[partial]
			: partial
		: undefined;

	const { isLoading: isLoadingUserInfo, ...userInfo } = useUserContext();
	const {
		error,
		isLoading: isLoadingPartial,
		route,
	} = useLayoutServicePlaceholder('jula-main', {
		isActive: Boolean(item) && isOpen && !isLoadingUserInfo,
		path: item,
		queryVars: partialQueryVars,
		body: userInfo && { context: filterTruthyValues(userInfo) },
	});
	const isLoading = isLoadingUserInfo || isLoadingPartial;

	return (
		<Popover
			dataCy={route?.name ? slugify(route.name) : undefined}
			isOpen={isOpen}
			title={heading}
			onClose={onClose}
			contentClassName="flex flex-col"
			shouldCloseOnOutsideClick={
				route?.fields?.closeOnOutsideClick?.value ?? true
			}
		>
			{isLoading && (
				<LoadingSkeleton>
					<div className="space-y-3">
						<LoadingSkeletonItem height="3rem" className="mb-6" />
						<LoadingSkeletonItem height="1.5rem" />
						<LoadingSkeletonItem height="1.5rem" />
						<LoadingSkeletonItem height="1.5rem" className="w-1/2" />
					</div>
				</LoadingSkeleton>
			)}
			{!isLoading && !error && route && (
				<div className="grow [&>*:first-child]:mt-0">
					<NestedPlaceholder<PartialPopoverProps>
						name="jula-main"
						rendering={route}
						partialProps={{ onPopoverClose: onClose, ...partialProps }}
					/>
				</div>
			)}
		</Popover>
	);
}
PartialPopover.displayName = 'PartialPopover';
